import { useMutation, useQueryClient } from "@tanstack/react-query";
import { baOrg } from "@/lib/ba-org-client";
import { removeTeamMember as removeTeamMemberApi } from "@/lib/teams-api";
import { useOrgId } from "@/lib/org-context";

export function useTeamMutations() {
  const qc = useQueryClient();
  const orgId = useOrgId();

  const invalidateList = () => qc.invalidateQueries({ queryKey: ["teams", orgId] });
  const invalidateDetail = (teamId: string) =>
    qc.invalidateQueries({ queryKey: ["team", orgId, teamId] });

  const createTeam = useMutation({
    mutationFn: (input: { name: string }) => baOrg.createTeam(input),
    onSuccess: () => invalidateList(),
  });

  const updateTeam = useMutation({
    mutationFn: (input: { teamId: string; name: string }) =>
      baOrg.updateTeam({ teamId: input.teamId, data: { name: input.name } }),
    onSuccess: (_data, vars) => {
      invalidateList();
      invalidateDetail(vars.teamId);
    },
  });

  const removeTeam = useMutation({
    mutationFn: (input: { teamId: string }) => baOrg.removeTeam(input),
    onSuccess: () => invalidateList(),
  });

  const addTeamMember = useMutation({
    mutationFn: (input: { teamId: string; userId: string }) => baOrg.addTeamMember(input),
    onSuccess: (_data, vars) => {
      invalidateList();
      invalidateDetail(vars.teamId);
    },
  });

  const removeTeamMember = useMutation({
    mutationFn: (input: { teamId: string; userId: string }) =>
      removeTeamMemberApi(input.teamId, input.userId),
    onSuccess: (_data, vars) => {
      invalidateList();
      invalidateDetail(vars.teamId);
    },
  });

  return { createTeam, updateTeam, removeTeam, addTeamMember, removeTeamMember };
}
